import { useQuery } from "@apollo/client";
import { FETCH_LOADING_PLAN } from "graphql/query";
import { getContainers } from "./ContainerizationView";

const getPercentage = (container) => {
  if (!container?.cbm) return 0;
  const value = ((container.cbm - container.emptySpace) / container.cbm) * 100;
  return Math.max(0, Math.min(100, value));
};

const Circle = ({ container }) => {
  const percentage = getPercentage(container);

  const progressColor =
    percentage >= 100 ? "#22c55e" : percentage >= 75 ? "#eab308" : "#ef4444";

  return (
    <div
      className="relative h-[30px] w-[30px] rounded-full transition-all duration-300 ease-out"
      style={{
        background: `conic-gradient(${progressColor} ${percentage * 3.6}deg, #d1d5db ${percentage * 3.6}deg)`,
      }}
      title={`${container.name}: ${percentage.toFixed(0)}% loaded`}
    >
      <span className="absolute inset-0 flex items-center justify-center text-[8px] font-semibold leading-none text-white">
        {container.name}
      </span>
    </div>
  );
};

const CircleContainerizationView = ({
  totalQty,
  totalCbm,
  shippingZone,
  defaultDisabledContainers = [],
  displayTotalCbm,
  showCost,
  className = "",
}) => {
  const numericTotalCbm = Number(totalCbm || 0);
  const isVeryLargeTotalCbm = numericTotalCbm > 5000;
  const roundedTotalCbm = Number(totalCbm || 0).toFixed(2);
  const displayedTotalCbm = displayTotalCbm ?? roundedTotalCbm;
  const hasDisabledContainers = defaultDisabledContainers.length > 0;
  const defaultSpecificContainers = hasDisabledContainers
    ? JSON.stringify(
        defaultDisabledContainers.reduce((acc, containerName) => {
          acc[containerName] = 0;
          return acc;
        }, {}),
      )
    : null;

  const { data } = useQuery(FETCH_LOADING_PLAN, {
    variables: {
      totalCbm: roundedTotalCbm,
      specificContainers: defaultSpecificContainers,
      rate: 1,
      zone: shippingZone,
    },
    skip: !shippingZone || numericTotalCbm <= 0 || isVeryLargeTotalCbm,
  });

  const containers = data?.loadingPlan
    ? getContainers(data.loadingPlan, Number(roundedTotalCbm)).containers
    : [];

  if (!containers.length) {
    return <span className={className}>{displayedTotalCbm}</span>;
  }

  if (isVeryLargeTotalCbm) {
    return (
      <div className={`flex flex-col items-end gap-1 ${className}`}>
        <span>{displayedTotalCbm}m³</span>
        <span className="text-red-500 whitespace-normal">
          The quantity is very large. Please confirm the input data is correct.
          If there is an issue, please contact our production team.
        </span>
      </div>
    );
  }

  const totalCost = data.loadingPlan.reduce(
    (acc, i) => acc + i.cost * i.qty,
    0,
  );

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`}>
      <div className="flex flex-wrap justify-end gap-1">
        {containers.map((c, i) => (
          <Circle container={c} key={`${c.name}-${i}`} />
        ))}
      </div>
      <span>{displayedTotalCbm}m³</span>

      {showCost && (
        <div className=" whinow">
          $
          {totalCost.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          / {totalQty} = $
          {(totalCost / totalQty).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      )}
    </div>
  );
};

export default CircleContainerizationView;
